import { useEffect, useState, type ReactNode } from 'react';

import { ApiError } from '../api/client.ts';
import { links, orders } from '../api/endpoints.ts';
import type { CartQuote, DeliveryTariff } from '../api/types.ts';
import { formatPara, para } from '../core/money.ts';
import { useStore } from '../core/store.ts';
import { errorText as textOf, useLang, useStrings, type Strings } from '../i18n/index.ts';
import { deliveryFee, etaText, GUEST_ADDRESS_ID, selectedAddress, tariffOf, useAddressBook } from '../state/address-book.ts';
import { cartStore, cartWeightG, clearCart, hasAgeRestricted, itemsTotal, lineTotal, orderItems, overweight, useCart, type CartChange } from '../state/cart.ts';
import { formatWeight } from '../core/nutrition.ts';
import { describeChange, fixCart } from '../state/cart-sync.ts';
import { ageGateStore, ageVerdict, askAge } from '../state/gates.ts';
import { presentPartnerPromoAfterPayment } from '../state/partner-promo.ts';
import { defaultMethod, goToPaymentPage, loadPaymentConfig, paymentConfigStore, payWithNewCard, payWithSavedCard, savedCardsStore, type CardInput, type PaymentMethod } from '../state/payment.ts';
import { CardForm } from '../ui/CardForm.tsx';
import { PaymentPicker } from '../ui/PaymentPicker.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { offerPushPrimer } from '../state/push.ts';
import { openOrder, openSignIn } from '../state/router.ts';
import { useSession } from '../state/session.ts';
import { isStorePaused, localClock, storeEtaText, storeInfoStore } from '../state/stores.ts';
import { trackOrder } from '../state/watcher.ts';
import { Icon, type IconName } from '../ui/icons.tsx';
import { BarButton, Button, Card, cx, ErrorBanner, FieldBox, LinkedText, ScreenTitle, Spinner, Toggle } from '../ui/kit.tsx';
import { PayMarks } from '../ui/PayMarks.tsx';
import { Screen } from '../ui/Screen.tsx';
import { SERBIA, splitPhone, type PhoneCountry } from '../core/phone-country.ts';
import { CountryCodeMenu } from '../ui/CountryCodeMenu.tsx';
import '../styles/cart.css';


/** Строка словаря со ссылкой в разметке `[текст](адрес)`. */
function RowIcon({ name }: { name: IconName }) {
  return <Icon name={name} size={20} className="checkout-row-icon" />;
}

/**
 * Оформление: адрес, сообщение курьеру, скидки и оплата.
 *
 * Заказ создаётся и оплачивается двумя вызовами: первый резервирует товар на складе, второй
 * списывает деньги. Резерв держится ограниченное время, поэтому оплата идёт сразу. Перед
 * созданием заказа наличие сверяется ещё раз: закончившийся товар не становится отказом,
 * корзина правится сама, а человек видит список правок и кнопку «Оформить ещё раз».
 */
export function Checkout(props: {
  /** Адрес меняется шагом того же листа. */
  onChangeAddress: () => void;
  /** Идёт оплата: лист на это время не закрывается. */
  onBusy: (busy: boolean) => void;
}) {
  const strings = useStrings();
  const lang = useLang();
  const session = useSession();
  const book = useAddressBook();
  const cart = useCart();
  // Способ оплаты приходит с сервера: перечитывается при каждом оформлении, чтобы смена
  // эквайера или тестовой карты вступила в силу без перезагрузки страницы.
  useEffect(() => {
    void loadPaymentConfig();
  }, []);
  const storeInfo = useStore(storeInfoStore);
  const leaveAtDoorEnabled = storeInfo?.leaveAtDoorEnabled ?? false;
  const ageGate = useStore(ageGateStore);

  // Номер для курьера с кодом страны из того же списка, что на входе; сохранённый номер
  // раскладывается на код и цифры.
  const savedContact = session.profile?.contactPhone ? splitPhone(session.profile.contactPhone) : null;
  const [contactCountry, setContactCountry] = useState<PhoneCountry>(savedContact?.country ?? SERBIA);
  const [contactPhoneDigits, setContactPhoneDigits] = useState(savedContact?.digits ?? '');
  const [comment, setComment] = useState('');
  const [leaveAtDoor, setLeaveAtDoor] = useState(false);
  const [promoInput, setPromoInput] = useState('');
  const [promoExpanded, setPromoExpanded] = useState(false);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [promoError, setPromoError] = useState<string | null>(null);
  const [isQuoting, setQuoting] = useState(false);
  const [quote, setQuote] = useState<CartQuote | null>(null);
  const [isPlacing, setPlacing] = useState(false);
  const [errorText, setErrorText] = useState<string | null>(null);
  /** Явное согласие с условиями перед оплатой: без него кнопка оплаты выключена (требование банка). */
  const [consent, setConsent] = useState(false);
  useStore(paymentConfigStore);
  useStore(savedCardsStore);
  const [method, setMethod] = useState<PaymentMethod>(defaultMethod);
  /** Заказ создан, карты у клиента нет: данные карты спрашиваются последним шагом, в листе. */
  const [cardEntryFor, setCardEntryFor] = useState<{ orderId: string; amount: bigint } | null>(null);
  /** Новая карта из формы: уходит эквайеру, когда заказ создан. */
  const [newCard, setNewCard] = useState<CardInput | null>(null);
  const [showsCardForm, setShowsCardForm] = useState(false);
  const [fixedChanges, setFixedChanges] = useState<CartChange[] | null>(null);

  const address = selectedAddress(book);
  const tariff = tariffOf(book);
  const ageRestricted = hasAgeRestricted(cart);
  const signedIn = session.profile !== null;
  const needsPhone = signedIn && session.profile?.phone == null;

  // Предпросмотр скидок: состав корзины, код и адрес (склад подарка). Негодный код снимается
  // с объяснением, остальные скидки остаются; без ответа сервера живёт прежний расчёт.
  const quoteKey = `${cart.lines.map((line) => `${line.productId}:${line.qty}`).join(',')}|${promoCode ?? ''}|${book.selectedId ?? ''}`;
  useEffect(() => {
    const state = cartStore.get();
    if (state.lines.length === 0) {
      setQuote(null);
      return;
    }
    let cancelled = false;
    setQuoting(true);
    orders
      .quote(orderItems(state), promoCode, book.selectedId)
      .then((next) => {
        if (!cancelled) setQuote(next);
      })
      .catch((error: unknown) => {
        if (cancelled || !(error instanceof ApiError) || error.code !== 'promo_code_invalid') return;
        setPromoError(strings.promoReason(String(error.details.reason ?? '')));
        setPromoCode(null);
      })
      .finally(() => {
        if (!cancelled) setQuoting(false);
      });
    return () => {
      cancelled = true;
    };
    // Пересчёт идёт по ключу; язык на расчёт не влияет.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteKey]);

  const { onBusy } = props;
  useEffect(() => {
    onBusy(isPlacing);
    return () => onBusy(false);
  }, [isPlacing, onBusy]);

  // Суммы. Подарок сервер считает и в товарах, и в скидке; в итоге корзины подарка нет,
  // поэтому его доля из скидки убирается, иначе подарок вычелся бы дважды.
  const items = itemsTotal(cart);
  const giftDiscount = (quote?.applied ?? []).filter((entry) => entry.kind === 'gift_threshold').reduce((sum, entry) => sum + para(entry.discountRsd), 0n);
  const discount = quote ? para(quote.discountRsd) - giftDiscount : 0n;
  const credit = para(quote?.creditUsedRsd);
  const codeDiscount = para(quote?.applied.find((entry) => entry.kind === 'promo_code')?.discountRsd);
  // Порог бесплатной доставки считается от суммы после скидки и до бонусов, как на сервере.
  const fee = deliveryFee(tariff, items - discount);
  const total = items - discount - credit + fee;

  // Кнопка гаснет только по причинам, видимым на экране, и всегда объясняет почему.
  const blocker = ((): string | null => {
    if (cart.lines.length === 0) return strings.cartEmpty;
    if (storeInfo && isStorePaused(storeInfo)) return strings.storePausedHint;
    if (storeInfo && !storeInfo.isOpen) return strings.storeClosedHint(localClock(storeInfo.opensAt, storeInfo.timezone));
    if (address?.deliverable !== true) return strings.outOfZone;
    if (ageRestricted && storeInfo && !storeInfo.alcoholSaleOpen && storeInfo.alcoholSaleFrom && storeInfo.alcoholSaleUntil) {
      return strings.alcoholClosedHint(storeInfo.alcoholSaleFrom, storeInfo.alcoholSaleUntil);
    }
    if (ageRestricted && ageVerdict(ageGate) === 'underage') return strings.ageGateUnderage;
    // Минимум по сумме после скидок, как на сервере: иначе оплата уйдёт и вернётся с order_too_small.
    const minimum = tariff ? para(tariff.minOrderRsd) : 0n;
    if (minimum > 0n && items - discount < minimum) return strings.addMoreToOrder(formatPara(minimum - (items - discount)));
    const over = overweight(tariff, cartWeightG(cart));
    if (over !== null) return strings.orderTooHeavy(formatWeight(over.actual, lang), formatWeight(over.limit, lang));
    return null;
  })();
  const canPay = blocker === null;

  const applyPromo = () => {
    const code = promoInput.trim().toUpperCase();
    if (!code) return;
    setPromoError(null);
    setPromoCode(code);
  };

  const dropPromo = (reason: string) => {
    setPromoError(strings.promoReason(reason));
    setPromoCode(null);
  };

  const placeAndPay = async (typedCard: CardInput | null = newCard) => {
    if (!signedIn || !address || address.id === GUEST_ADDRESS_ID || isPlacing) return;
    setErrorText(null);
    setFixedChanges(null);
    setPlacing(true);
    try {
      // Сверка перед списанием: заказ с закончившимся товаром сервер всё равно отклонит, а
      // человеку лучше увидеть правку заранее. Сервер не ответил — оформляем как есть.
      const ids = cartStore.get().lines.map((line) => line.productId);
      const changes = await fixCart(ids);
      if (changes && changes.length > 0) {
        setFixedChanges(changes);
        return;
      }

      const state = cartStore.get();
      const phone = contactPhoneDigits.length >= 8 ? contactCountry.dialCode + contactPhoneDigits : undefined;
      let created;
      try {
        created = await orders.create({
          addressId: address.id,
          items: orderItems(state),
          comment: comment.trim() || undefined,
          leaveAtDoor: leaveAtDoorEnabled && leaveAtDoor && !hasAgeRestricted(state),
          promoCode: promoCode ?? undefined,
          contactPhone: needsPhone ? phone : undefined,
        });
      } catch (error) {
        if (!(error instanceof ApiError)) {
          setErrorText(strings.somethingWentWrong);
          return;
        }
        // Товар мог закончиться между сверкой и оформлением: корзина правится, вместо отказа
        // предлагается повтор. Заказ на этом ответе не создан.
        if (error.code === 'insufficient_stock' || error.code === 'product_unavailable') {
          setFixedChanges((await fixCart(ids)) ?? []);
          return;
        }
        // В профиле нет даты рождения (корзина собрана раньше): спрашиваем здесь же.
        if (error.code === 'age_restricted' && error.details.reason === 'unconfirmed') {
          askAge(null);
          return;
        }
        // Код перестал действовать между предпросмотром и оформлением.
        if (error.code === 'promo_code_invalid') {
          dropPromo(String(error.details.reason ?? ''));
          return;
        }
        setErrorText(textOf(strings, error));
        return;
      }

      // Заказ создан и держит резерв. Чем бы ни кончилась оплата, человек попадает на его
      // экран: иначе он оформит второй, пока первый висит до конца резерва. Отказ карты и
      // 3-D Secure оставляют корзину на месте, повтор оплаты идёт с экрана заказа.
      // На странице эквайера браузер уходит из приложения; корзина очистится после подтверждения оплаты.
      if (method.kind === 'bank') {
        trackOrder(created.id, 'PAYMENT_PENDING');
        const outcome = await goToPaymentPage(created.id).catch(() => 'declined' as const);
        if (outcome === 'redirect') return;
        if (outcome === 'paid') clearCart();
        openOrder(created.id);
        return;
      }
      if (method.kind === 'new') {
        trackOrder(created.id, 'PAYMENT_PENDING');
        if (typedCard) {
          setNewCard(null);
          await submitCard(created.id, typedCard);
        } else {
          setCardEntryFor({ orderId: created.id, amount: total });
        }
        return;
      }
      const payment =
        method.kind === 'saved'
          ? await payWithSavedCard(created.id, method.cardId).catch(() => null)
          : await orders.pay(created.id, { cardToken: 'mock-card' }).catch(() => null);
      const paid = payment?.status === 'paid';
      if (paid) clearCart();
      trackOrder(created.id, paid ? 'PAID' : 'PAYMENT_PENDING');
      if (paid) {
        presentPartnerPromoAfterPayment();
        // Разрешение на уведомления спрашивается после первого заказа: теперь понятно, зачем оно.
        void offerPushPrimer();
      }
      openOrder(created.id);
    } finally {
      setPlacing(false);
    }
  };

  /** Форма карты отправлена: браузер уходит к эквайеру; отказ до ухода показывается в заказе. */
  const submitCard = async (orderId: string, card: CardInput) => {
    setPlacing(true);
    try {
      const outcome = await payWithNewCard(orderId, card);
      if (outcome === 'redirect') return;
      if (outcome === 'paid') clearCart();
      setCardEntryFor(null);
      openOrder(orderId);
    } catch (error) {
      setErrorText(textOf(strings, error));
      setCardEntryFor(null);
      openOrder(orderId);
    } finally {
      setPlacing(false);
    }
  };

  const eta = etaText(book, strings) ?? storeEtaText(storeInfo);

  const totalParts = [`${strings.itemsTotal} ${formatPara(items)}`];
  if (discount > 0n) totalParts.push(`${strings.discount} −${formatPara(discount)}`);
  if (credit > 0n) totalParts.push(`${strings.bonusUsed} −${formatPara(credit)}`);
  totalParts.push(`${strings.deliveryFee} ${fee === 0n ? strings.deliveryFree.toLocaleLowerCase(lang) : formatPara(fee)}`);

  return (
    <Screen embedded className="cart-screen">
      <div className="cart-scroll readable stack">
        <ScreenTitle text={strings.checkoutTitle} />

        <Card flush>
          <button type="button" className="checkout-address" onClick={props.onChangeAddress}>
            <span className="grow stack">
              <span className="t-footnote t-muted">{strings.deliveryTo}</span>
              <b>{address?.text ?? strings.chooseAddress}</b>
              {address?.comment && <span className="t-footnote t-muted">{address.comment}</span>}
              {eta && (
                <span className="checkout-eta">
                  <Icon name="clock" size={14} weight={2.2} />
                  {eta}
                </span>
              )}
            </span>
            <Icon name="chevronRight" size={16} weight={2.6} className="t-muted" />
          </button>
        </Card>

        {needsPhone && (
          <Card>
            <div className="stack" style={{ gap: 8 }}>
              <FieldBox label={`${strings.phoneForCourierTitle} · ${strings.phoneOptional}`} muted>
                <CountryCodeMenu country={contactCountry} lang={lang} label={strings.phoneLabel} onChange={setContactCountry} />
                <input
                  className="t-numeric"
                  type="tel"
                  inputMode="tel"
                  autoComplete="tel"
                  placeholder={contactCountry === SERBIA ? strings.phonePlaceholder : ''}
                  value={contactPhoneDigits}
                  onChange={(event) => {
                    // Автозаполнение вставляет номер целиком: код уходит в меню, в поле остаются цифры.
                    const full = splitPhone(event.target.value);
                    if (full) {
                      setContactCountry(full.country);
                      setContactPhoneDigits(full.digits.slice(0, 12));
                    } else {
                      setContactPhoneDigits(event.target.value.replace(/\D/g, '').slice(0, 12));
                    }
                  }}
                />
              </FieldBox>
              <span className="t-caption t-muted">{strings.phoneForCourierText}</span>
            </div>
          </Card>
        )}

        <OptionsGroup
          strings={strings}
          ageRestricted={ageRestricted}
          leaveAtDoorEnabled={leaveAtDoorEnabled}
          leaveAtDoor={leaveAtDoor}
          onLeaveAtDoor={setLeaveAtDoor}
          comment={comment}
          onComment={setComment}
        />

        <Card flush className="checkout-group">
          {promoCode && quote?.promoCode != null ? (
            <div className="checkout-row">
              <RowIcon name="tag" />
              <span className="grow stack">
                <span>{promoCode}</span>
                <span className="t-footnote checkout-accent">{strings.promoApplied(formatPara(codeDiscount))}</span>
              </span>
              <button
                type="button"
                className="checkout-row-action is-muted"
                onClick={() => {
                  setPromoCode(null);
                  setPromoInput('');
                  setPromoError(null);
                  setPromoExpanded(false);
                }}
              >
                {strings.promoRemove}
              </button>
            </div>
          ) : promoExpanded ? (
            <div className="checkout-row is-column">
              <form
                className="row"
                style={{ gap: 12 }}
                onSubmit={(event) => {
                  event.preventDefault();
                  applyPromo();
                }}
              >
                <RowIcon name="tag" />
                <input
                  className="plain-input"
                  aria-label={strings.promoCode}
                  placeholder={strings.promoCodePlaceholder}
                  autoCapitalize="characters"
                  autoCorrect="off"
                  autoComplete="off"
                  spellCheck={false}
                  enterKeyHint="done"
                  autoFocus
                  value={promoInput}
                  onChange={(event) => setPromoInput(event.target.value)}
                />
                <button type="submit" className="checkout-row-action" disabled={promoInput.trim() === '' || isQuoting}>
                  {strings.promoApply}
                </button>
              </form>
              {promoError && <span className="t-footnote checkout-promo-error">{promoError}</span>}
            </div>
          ) : (
            <button type="button" className="checkout-row" onClick={() => setPromoExpanded(true)}>
              <RowIcon name="tag" />
              <span className="grow">{strings.promoCode}</span>
              <Icon name="chevronRight" size={14} weight={2.8} className="t-muted" />
            </button>
          )}
          {quote?.gifts.map((gift) => (
            <div key={gift.productId} className="checkout-row">
              <RowIcon name="gift" />
              <span className="grow">{strings.giftLine(gift.name)}</span>
              <span className="t-numeric checkout-accent">{strings.deliveryFree}</span>
            </div>
          ))}
          {quote && para(quote.creditBalanceRsd) > 0n && (
            <div className="checkout-row">
              <RowIcon name="gift" />
              <span className="grow stack">
                <span>{strings.bonusUsed}</span>
                <span className="t-footnote t-muted">{strings.bonusOnAccount(formatPara(para(quote.creditBalanceRsd)))}</span>
              </span>
              {credit > 0n && <span className="t-numeric checkout-accent">−{formatPara(credit)}</span>}
            </div>
          )}
        </Card>

        {/* Состав с ценой за единицу перед оплатой: требование банка-эквайера. */}
        <Card>
          <div className="stack checkout-spec" style={{ gap: 8 }}>
            {cart.lines.map((line) => (
              <div key={line.productId} className="checkout-spec-line">
                <span className="checkout-spec-name">
                  {line.name}
                  <small className="t-footnote t-muted">
                    {line.qty} × {formatPara(para(line.priceRsd))}
                  </small>
                </span>
                <span className="t-numeric">{formatPara(lineTotal(line))}</span>
              </div>
            ))}
          </div>
        </Card>

        <div className="stack" style={{ gap: 8 }}>
          <PaymentPicker
            method={method}
            newCardDigits={newCard?.pan}
            onChange={(next) => {
              setMethod(next);
              if (next.kind === 'new') setShowsCardForm(true);
            }}
          />
          <PayMarks />
        </div>

        <label className="checkout-consent">
          <input type="checkbox" checked={consent} onChange={(event) => setConsent(event.target.checked)} />
          <span>
            <LinkedText text={strings.consentTerms(links.terms(lang))} />
          </span>
        </label>

        <div className="checkout-total">
          <span className="t-footnote t-muted">{strings.total}</span>
          <b className="t-numeric">{formatPara(total)}</b>
          <span className="t-footnote t-muted">{totalParts.join(' · ')}</span>
          <span className="t-footnote t-muted">{strings.vatIncluded}</span>
        </div>

        {fixedChanges && (
          <Card>
            <div className="stack" style={{ gap: 10 }}>
              <b className="t-display" style={{ fontSize: 17 }}>
                {strings.cartUpdated}
              </b>
              <span className="t-subheadline t-muted">{strings.cartUpdatedHint}</span>
              {fixedChanges.map((change) => (
                <span key={change.productId} className="t-subheadline" style={{ fontWeight: 500 }}>
                  {describeChange(change, strings)}
                </span>
              ))}
              <Button disabled={!canPay || isPlacing} onClick={() => void placeAndPay()}>
                {strings.checkAgain}
              </Button>
            </div>
          </Card>
        )}

        {errorText && <ErrorBanner text={errorText} />}
      </div>

      <div className="bottom-bar cart-bar">
        <div className="readable stack" style={{ gap: 10 }}>
          {blocker && <p className="checkout-blocker">{blocker}</p>}
          {signedIn ? (
            <BarButton disabled={!canPay || !consent || isPlacing} onClick={() => (method.kind === 'new' && !newCard ? setShowsCardForm(true) : void placeAndPay())}>
              {isPlacing ? (
                <>
                  <Spinner size={18} />
                  {strings.paying}
                </>
              ) : (
                strings.payAmount(formatPara(total))
              )}
            </BarButton>
          ) : (
            <BarButton onClick={openSignIn}>{strings.signInToContinue}</BarButton>
          )}
        </div>
      </div>
      {showsCardForm && (
        <Sheet size="auto" label={strings.newCard} onClose={() => setShowsCardForm(false)}>
          {(close) => {
            // С принятыми условиями форма сразу платит, иначе только запоминает карту:
            // оплата идёт кнопкой под согласием.
            const readyToPay = signedIn && canPay && consent && !isPlacing;
            return (
              <>
                <div className="sheet-header">
                  <button type="button" className="text-button" onClick={close}>
                    {strings.cancel}
                  </button>
                  <h2>{strings.newCard}</h2>
                  <span />
                </div>
                <div className="sheet-body sheet-pad stack">
                  <CardForm
                    amountText={formatPara(total)}
                    busy={false}
                    owner={session.profile?.name ?? ''}
                    {...(readyToPay ? {} : { submitTitle: strings.done })}
                    onSubmit={(card) => {
                      setNewCard(card);
                      setShowsCardForm(false);
                      if (readyToPay) void placeAndPay(card);
                    }}
                  />
                </div>
              </>
            );
          }}
        </Sheet>
      )}
      {cardEntryFor && (
        <Sheet size="auto" locked={isPlacing} label={strings.newCard} onClose={() => { if (!isPlacing) { setCardEntryFor(null); openOrder(cardEntryFor.orderId); } }}>
          {(close) => (
            <>
              <div className="sheet-header">
                <button type="button" className="text-button" disabled={isPlacing} onClick={close}>
                  {strings.cancel}
                </button>
                <h2>{strings.newCard}</h2>
                <span />
              </div>
              <div className="sheet-body sheet-pad stack">
                <CardForm amountText={formatPara(cardEntryFor.amount)} busy={isPlacing} owner={session.profile?.name ?? ''} onSubmit={(card) => void submitCard(cardEntryFor.orderId, card)} />
              </div>
            </>
          )}
        </Sheet>
      )}
    </Screen>
  );
}

/** Опции доставки строками одной карточки: переключатель и сообщение курьеру. */
function OptionsGroup(props: {
  strings: Strings;
  ageRestricted: boolean;
  leaveAtDoorEnabled: boolean;
  leaveAtDoor: boolean;
  onLeaveAtDoor: (next: boolean) => void;
  comment: string;
  onComment: (next: string) => void;
}): ReactNode {
  const { strings, ageRestricted } = props;
  return (
    <Card flush className="checkout-group">
      {props.leaveAtDoorEnabled ? (
      <div className={cx('checkout-row', ageRestricted && 'is-disabled')}>
        <span className="grow stack">
          <span>{strings.leaveAtDoor}</span>
          <span className="t-footnote t-muted">{ageRestricted ? strings.leaveAtDoorBlocked : strings.leaveAtDoorHint}</span>
        </span>
        {/* Заказ с товаром 18+ у двери не оставляют: курьер проверяет документ. */}
        <Toggle
          label={strings.leaveAtDoor}
          checked={props.leaveAtDoor && !ageRestricted}
          disabled={ageRestricted}
          onChange={props.onLeaveAtDoor}
        />
      </div>
      ) : null}
      <label className="checkout-row">
        <RowIcon name="chat" />
        <input
          className="plain-input"
          placeholder={strings.commentToCourier}
          aria-label={strings.commentToCourier}
          value={props.comment}
          onChange={(event) => props.onComment(event.target.value)}
        />
      </label>
    </Card>
  );
}
